import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../services/api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      auth.getProfile()
        .then((res) => setUser(res.data))
        .catch(() => {
          localStorage.removeItem('token');
          setToken(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (credentials) => {
    const { data } = await auth.login(credentials);
    localStorage.setItem('token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  // Entrada amb el codi imprès de l'examen. El servidor diu on ha d'aterrar el
  // grup: si té un exercici a mitges hi torna, i si no, va al cas d'aquest examen.
  const loginAmbCodi = async (codi) => {
    const { data } = await auth.entraAmbCodi(codi);
    localStorage.setItem('token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const register = async (userData) => {
    const { data } = await auth.register(userData);
    // New users require approval - no token is returned
    // The response will have requiresApproval: true
    return data;
  };

  // Amb sessió única per usuari, esborrar el token del navegador no n'invalidava
  // cap: el hash es quedava a Redis fins a caducar. Cal avisar el servidor.
  // Si la crida falla —xarxa caiguda, token ja caducat— es tanca igualment en
  // local: el pitjor que pot passar és deixar una sessió òrfena a Redis, i deixar
  // l'usuari atrapat a dins seria pitjor.
  const logout = async () => {
    try {
      await auth.logout();
    } catch (err) {
      // Es tanca la sessió en local de totes maneres.
    } finally {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    }
  };

  const updateUser = (updatedUser) => {
    setUser(prev => prev ? { ...prev, ...updatedUser } : updatedUser);
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    loginAmbCodi,
    register,
    logout,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
